(ns app.front.view.admin.entity-lookup
  (:require [app.front.entities :as entities]
            [app.front.ui :as ui]
            [app.front.util :as futil]
            [app.front.view.admin.entity-table :as entable]
            [app.i18n.model :as i18n-model]
            [clojure.edn :as edn]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-chain.core :as rc]
            [re-frame.core-instrumented :as rf]
            [schema.core :as s]))


(def entity-desc-keys
  "Keys that are checked in order for the title doc text for an entity"
  [:db/doc
   :group/name
   :workspace/id
   :filter/name
   :user/email
   :form.schema/name
   :inspection/names
   :inspection.template.kind/names
   :integration/work-type
   :integration/work-target
   :party/email
   :localized-string/text
   :municipality/number
   :user/first-name
   :project/name
   :verdict.section/content
   :verdict.template/name
   :file/filename
   :group.config/target])


(defn- parse-conditions
  "Returns a collection of Datomic lookup refs if the string can be parsed as such.
   Also accepts a single lookup ref for convenience."
  [s]
  (try
    (let [data (edn/read-string s)]
      (when (and (vector? data)
                 (not-empty data))
        (if (-> data first vector? not)
          [data]
          data)))
    (catch js/Error _
      nil)))


(rc/reg-chain ::fetch-entities
  (fn [{:keys [db]} [_ search-term]]
    (let [conditions (parse-conditions search-term)
          entity-id  (futil/parse-int search-term)]
      (merge {:db (dissoc db ::entity-ids)}
             (cond
               (some? entity-id)  {:comm/command {:command :admin/get-entity-by-id
                                                  :data    {:id entity-id}}}
               (some? conditions) {:comm/command {:command :admin/get-entities-by-conditions
                                                  :data    {:conditions conditions}}}
               :else              {:comm/command {:command :admin/get-entities-by-regex
                                                  :data    {:search-term search-term}}}))))

  (fn [{:keys [db]} [_ _ status results]]
    {:db         (assoc db ::entity-ids (keys results))
     :dispatch-n [[::entities/override-entity-db status results]
                  (when (empty? results)
                    [:notifications/add :toast :warning :warning/warning :entity-lookup/no-entities])]}))


(rc/reg-chain ::fetch-entity-modified-data
  (fn [{:keys [db]} [_ entity-id]]
    {:db (assoc-in db [::entity-modified-data entity-id] {:state :loading})})

  (fn [_ [_ entity-id]]
    {:comm/command {:command :admin/get-entity-modified-data
                    :data    {:entity-id entity-id}}})

  (fn [{:keys [db]} [_ entity-id status results]]
    (if (= 200 status)
      {:db (assoc-in db [::entity-modified-data entity-id] (assoc results :state :loaded))}
      {:db db})))


(rf/reg-sub ::entity-modified-data
  (fn [db [_ entity-id]]
    (get-in db [::entity-modified-data entity-id] {:state :unloaded})))


(rc/reg-chain ::fetch-tx-changes
  (fn [_ [_ transaction-id]]
    {:comm/command {:command :admin/get-tx-changes
                    :data    {:transaction-id transaction-id}}})

  (fn [{:keys [db]} [_ transaction-id status results]]
    (if (= 200 status)
      {:db (assoc-in db [::tx-changes transaction-id] (for [[entity attribute value added?] results]
                                                        (util/mmap entity attribute value added?)))}
      {:db db})))


(rf/reg-event-db ::hide-changes
  (fn [db [_ transaction-id]]
    (p/dissoc-in db [::tx-changes transaction-id])))


(rf/reg-sub ::tx-changes
  (fn [db [_ transaction-id]]
    (get-in db [::tx-changes transaction-id])))


(defn url [search-term]
  (href/href :view/admin.entity-lookup-with-param
             {:search-term (str/trim search-term)}))

(rf/reg-event-fx ::lookup
  (fn [_ [_ search-term]]
    {:route/push {:route  :view/admin.entity-lookup-with-param
                  :params {:search-term (str/trim search-term)}}}))

(rf/reg-sub ::entity-ids :-> ::entity-ids)


(rf/reg-sub
  ::entities
  :<- [::entities/entity-db]
  :<- [::entity-ids]
  (fn [[entity-db entity-ids]]
    (->> entity-ids
         (select-keys entity-db)
         vals
         (sort-by (juxt :entity/type :db/txInstant :db/id)))))


(rf/reg-sub ::search-term :-> ::search-term)


(rf/reg-event-db ::save-search-term
  (fn [db [_ search-term]]
    (assoc db ::search-term search-term)))


(defn str-or-tr [lang data]
  (if (or (seq? data) (set? data))
    (i18n-model/get-text data lang)
    data))

(defn entity-view [data tr]
  (let [id        (:db/id data)
        modified  (<sub [::entity-modified-data id])
        changes   (<sub [::tx-changes id])
        is-group? (= :entity.type/group (:entity/type data))
        is-user?  (= :entity.type/user (:entity/type data))
        is-tx?    (some? (:db/txInstant data))
        lang      (<sub [:language])]
    [:section.bx--row.audit-log__tx {:key id}
     [:div.audit-log__tx-header.bx--col-xs-12.flex.justify-between.items-baseline
      [:div.capitalize.flex-0 (or (:entity/type data)
                                  (when (-> data :db/txInstant some?)
                                    (tr [:entity-lookup/transaction]))
                                  (tr [:entity-lookup/unknown]))]
      [:div id]
      [:div.text-gray-400
       (->> (apply util/get-some data entity-desc-keys)
            (str-or-tr lang)
            str)]
      [:div.flex.justify-end.flex-1
       (when-not (or is-user? is-tx?)
         (cc/button
           {:size :small
            :kind :secondary
            :href (url (str "[:entity/parent " id "]"))}
           "View children"))
       (when is-group?
         (cc/button
           {:size       :small
            :kind       :secondary
            :class-name "ml-2"
            :href       (url (str "[:user/groups " id "]"))}
           "View members"))]]
     [:div.audit-log__tx-changes.bx--col-xs-12
      [:div.absolute.right-1.top-1
       (if is-tx?
         [:button
          {:on-click #(if (some? changes)
                        (>evt [::hide-changes id])
                        (>evt [::fetch-tx-changes id]))}
          (if (some? changes)
            "Hide changes"
            "Show changes")]
         (case (:state modified)
           :unloaded [:button
                      {:on-click #(>evt [::fetch-entity-modified-data id])}
                      "Show modified"]
           :loading  (cc/loading-spinner {:small        true
                                          :with-overlay false
                                          :class-name   "mr-4"})
           :loaded   nil))]
      [entable/entity-table
       {:id     (str "lookup-" (when (some? changes) "changes-") id)
        :title  "Entity data"
        :datoms (if (some? changes)
                  changes
                  (map (fn [[k v]]
                         (let [loaded? (= :loaded (:state modified))
                               tx      (when loaded? (get modified k {}))
                               datom   {:entity    id
                                        :attribute k
                                        :value     v}]
                           (p/assoc-if datom :tx tx)))
                       data))
        :hidden (if (some? changes)
                  #{}
                  #{:entity})}]]]))


(defn validate-search-term [term]
  (and (not (str/blank? term))
       (< 2 (count (str/trim term)))))


(defn view [_]
  (let [tr          (<sub [:tr])
        search-term (<sub [::search-term])
        entities    (<sub [::entities])]
    [:div.cloudpermit-page.admin-page
     [:div.bx--row.page-heading
      [:h1.bx--col-xs-12 (tr [:admin-tools/entity-lookup])]]
     [:section.bx--row.items-end
      [ui/text-input {:id         "entity-lookup-id"
                      :type       :search
                      :value      search-term
                      :on-change  #(rf/dispatch-sync [::save-search-term (-> % .-target .-value)])
                      :on-key-up  #(when (and (futil/enter-press? %)
                                              (validate-search-term search-term))
                                     (>evt [::lookup search-term]))
                      :label-text (tr [:entity-lookup/search-label])}]
      [:div.bx--col-xs-3.bx--col-md-3.bx--col-xl-2
       (cc/button {:kind       :primary
                   :class-name "min-h-0 h-10 py-1 pl-3 pr-4 w-auto"
                   :disabled   (not (validate-search-term search-term))
                   :on-click   #(>evt [::lookup search-term])}
                  (tr [:entity-lookup/search]))]]
     (futil/for-frag [entity entities]
       [entity-view (dissoc entity :app.front.entities/gen) tr])]))


(def route
  {:name :view/admin.entity-lookup
   :view view})


(def params-route
  {:name        :view/admin.entity-lookup-with-param
   :view        view
   :parameters  {:path {:search-term s/Str}}
   :controllers [{:parameters {:path [:search-term]}
                  :start      (fn [params]
                                (>evt [::fetch-entities (-> params :path :search-term)])
                                (>evt [::save-search-term (-> params :path :search-term)]))}]})
